import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import {
  summarizeManualBook,
  summarizeShortBook,
  summarizeFuturesSide,
} from '@/components/portfolio-toolbar/domain/computePortfolioBook'
import {
  DEFAULT_LOT_QTY,
  DEFAULT_NEAR_PCT,
  DEFAULT_FAR_PCT,
  DEFAULT_TARGET_MARGIN_PCT,
  computeHoverSim,
} from './domain/computeHoverSim'

function toLongLots(ledger) {
  return Object.values(ledger || {})
    .filter((u) => u && u.bucket === 'manual' && Number(u.current?.qty || 0) > 0)
    .map((u) => ({
      price: Number(u.current?.avgPrice || 0),
      qty: Number(u.current?.qty || 0),
    }))
}

function toShortLots(shortLedger) {
  return Object.values(shortLedger || {})
    .filter((u) => u && u.status === 'active' && Number(u.remainingQty || u.origin?.qty || 0) > 0)
    .map((u) => ({
      price: Number(u.origin?.sellPrice || u.sellPrice || 0),
      qty: Number(u.remainingQty || u.origin?.qty || 0),
    }))
}

export function useHoverSim() {
  const store = useStore()

  const visible = ref(false)
  const lotQty = ref(DEFAULT_LOT_QTY)
  const nearPct = ref(DEFAULT_NEAR_PCT)
  const farPct = ref(DEFAULT_FAR_PCT)
  const marginInputPct = computed({
    get: () => Number(store.state.targetMarginPct) || DEFAULT_TARGET_MARGIN_PCT,
    set: (val) => store.commit('setTargetMarginPct', val),
  })

  function currentBook() {
    const manual = summarizeManualBook(store.state.ledger)
    const shorts = summarizeShortBook(store.state.shortLedger)
    const futLong = summarizeFuturesSide(store.state.futuresInfos, 'long')
    const futShort = summarizeFuturesSide(store.state.futuresInfos, 'short')

    return {
      longsQty: manual.qty + futLong.qty,
      longsNotional: manual.notional + futLong.notional,
      shortsQty: shorts.remainingQty + futShort.qty,
      shortsNotional: shorts.remainingNotional + futShort.notional,
      longLots: toLongLots(store.state.ledger),
      shortLots: toShortLots(store.state.shortLedger),
      targetMarginPct: marginInputPct.value,
    }
  }

  function previewAt(hoverPrice, currentPrice) {
    return computeHoverSim({
      hoverPrice,
      currentPrice,
      qty: lotQty.value,
      nearPct: nearPct.value,
      farPct: farPct.value,
      ...currentBook(),
    })
  }

  function toggleVisible() {
    visible.value = !visible.value
  }

  function handleKeyDown(e) {
    if (e.target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return
    if (e.key === 'h' || e.key === 'H') toggleVisible()
  }

  onMounted(() => {
    window.addEventListener('keydown', handleKeyDown)
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyDown)
  })

  return {
    visible,
    lotQty,
    nearPct,
    farPct,
    marginInputPct,
    toggleVisible,
    previewAt,
  }
}
